import type { ReactNode } from 'react';
import classnames from 'classnames';
import React from 'react';

import type { UseDeliveryMessagesProps } from 'src/hooks';
import { useDeliveryMessages } from 'src/hooks';

import type { SvgIconNames } from '../Svg/icons-types';
import { Svg } from '../Svg/Svg';
import { RichText } from '../RichText/RichText';

import classes from './ProductDeliveryOption.scss';

type ProductDeliveryOptionProps = UseDeliveryMessagesProps & {
    /**
     * Render additional content within the delivery option.
     * For example a call to action button or link.
     */
    children?: ReactNode;
    iconName: SvgIconNames;
    isAvailable?: boolean;
    name: string;
};

export function ProductDeliveryOption(props: ProductDeliveryOptionProps) {
    const { children, iconName, isAvailable, name, ...rest } = props;

    const { availableMessage, unavailableMessage } = useDeliveryMessages(rest);

    const messaging = isAvailable ? availableMessage : unavailableMessage;
    const stockIconName = isAvailable ? 'stock-available' : 'stock-unavailable';

    return (
        <div
            className={classnames({
                [classes['root']]: true,
                [classes['optionUnavailable']]: !isAvailable,
            })}
        >
            <div className={classes['optionIcon']}>
                <Svg name={iconName} />
            </div>

            <div className={classes['optionMessage']}>
                <span className={classes['optionTitle']}>{name}</span>
                <div className={classes['optionContent']}>
                    <Svg name={stockIconName} />

                    <div className={classes['ctaContainer']}>
                        <RichText className={classes['cta']} content={messaging ?? ''} />
                        {isAvailable && children}
                    </div>
                </div>
            </div>
        </div>
    );
}

type DeliveryMethodProps = Omit<ProductDeliveryOptionProps, 'iconName' | 'name' | 'deliveryType'>;

export function ClickAndCollect(props: DeliveryMethodProps) {
    return (
        <ProductDeliveryOption
            {...props}
            deliveryType="clickAndCollect"
            iconName="click-collect"
            name="Click & Collect"
        />
    );
}

export function HomeDelivery(props: DeliveryMethodProps) {
    return <ProductDeliveryOption {...props} deliveryType="homeDelivery" iconName="delivery" name="Home Delivery" />;
}
